import { type ChangeEvent, useState } from 'react';
import type { Api } from '../api.js';
import { day, dollars } from '../format.js';
import type { BankAccount, ImportOutcome } from './types.js';

const NEEDS_OPENING = 'Enter the opening balance shown on the bank statement for this period.';

interface Ticket {
  key: string;
  url: string;
  headers: Record<string, string>;
}

function formatOf(file: File, content: string): 'ofx' | 'csv' {
  if (/\.(ofx|qfx)$/i.test(file.name) || /<OFX>/i.test(content)) return 'ofx';
  return 'csv';
}

export function UploadStatement(props: {
  api: Api;
  orgId: string;
  account: BankAccount;
  onImported: () => void;
}) {
  const { api, orgId, account } = props;
  const [file, setFile] = useState<{ name: string; format: 'ofx' | 'csv'; content: string } | null>(
    null,
  );
  const [opening, setOpening] = useState('');
  const [pdf, setPdf] = useState<File | null>(null);
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function check(
    next: { name: string; format: 'ofx' | 'csv'; content: string },
    openingValue = opening,
  ) {
    setBusy(true);
    setError('');
    try {
      setOutcome(
        await api.send<ImportOutcome>(orgId, 'POST', `/bank-accounts/${account.id}/imports`, {
          format: next.format,
          fileName: next.name,
          content: next.content,
          opening: openingValue,
          dryRun: true,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that file.');
    } finally {
      setBusy(false);
    }
  }

  async function choose(event: ChangeEvent<HTMLInputElement>) {
    const f = event.target.files?.[0];
    if (!f) return;
    const content = await f.text();
    const next = { name: f.name, format: formatOf(f, content), content };
    setFile(next);
    setOutcome(null);
    await check(next);
  }

  async function uploadPdf(): Promise<string | undefined> {
    if (!pdf) return undefined;
    const ticket = await api.send<Ticket>(orgId, 'POST', '/uploads', {
      kind: 'statement',
      contentType: 'application/pdf',
    });
    const res = await fetch(ticket.url, { method: 'PUT', headers: ticket.headers, body: pdf });
    if (!res.ok) throw new Error('The PDF statement could not be uploaded. Try again.');
    return ticket.key;
  }

  async function confirm() {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const statementKey = await uploadPdf();
      await api.send(orgId, 'POST', `/bank-accounts/${account.id}/imports`, {
        format: file.format,
        fileName: file.name,
        content: file.content,
        opening,
        ...(statementKey ? { statementKey } : {}),
      });
      setFile(null);
      setOutcome(null);
      setOpening('');
      setPdf(null);
      props.onImported();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The import did not go through.');
    } finally {
      setBusy(false);
    }
  }

  const needsOpening = outcome?.problems.includes(NEEDS_OPENING);
  const s = outcome?.summary;
  return (
    <div className="stack notice">
      <label>
        Upload a statement from {account.institution ?? 'your bank'} (OFX or CSV)
        <input type="file" accept=".ofx,.qfx,.csv,text/csv" onChange={choose} disabled={busy} />
      </label>
      {needsOpening && file && (
        <form
          className="title-row"
          onSubmit={(e) => {
            e.preventDefault();
            void check(file);
          }}
        >
          <label className="grow">
            Opening balance on the bank statement (first import only)
            <input
              inputMode="decimal"
              value={opening}
              onChange={(e) => setOpening(e.target.value)}
              placeholder="682.38"
              required
            />
          </label>
          <button type="submit" disabled={busy}>
            Check
          </button>
        </form>
      )}
      {outcome && !outcome.ok && !(needsOpening && outcome.problems.length === 1) && (
        <ul className="error">
          {outcome.problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {outcome?.ok && s && (
        <div className="stack">
          <p>
            <strong>
              {s.periodStart && day(s.periodStart)} to {s.periodEnd && day(s.periodEnd)}
            </strong>
            : {s.lines} transactions. Opening {s.opening && dollars(s.opening)}, money in{' '}
            {dollars(s.moneyIn)}, money out {dollars(s.moneyOut)}, closing{' '}
            {s.closing && dollars(s.closing)}. Everything adds up.
          </p>
          <label>
            Attach the PDF statement (recommended, kept as evidence)
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setPdf(e.target.files?.[0] ?? null)}
            />
          </label>
          <button type="button" onClick={confirm} disabled={busy}>
            {busy ? 'Importing' : `Import ${s.lines} transactions`}
          </button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
